import type { SeatDetail } from './types'
import { familyRoutes } from './familyRoutes'
import { STATUS_LABELS, LABELS } from './labels'
import {
  CARD_WIDTH,
  cardAssignees,
  cardResponsibilities,
  hiddenAssigneeCount,
  hiddenResponsibilityCount,
  showsStatusRow,
} from './cardMetrics'
import type { CardDensity } from './cardMetrics'

export const PDF_PAGE_FORMAT = 'letter'
export const PDF_PAGE_ORIENTATION = 'landscape'
export const PDF_MARGIN_IN = 0.4
export const PDF_PAGE_W_IN = 11
export const PDF_PAGE_H_IN = 8.5

export const PRINT_NODE_W_PX = CARD_WIDTH
/** Fallback only: cards carry their own measured height from the layout. */
export const PRINT_NODE_H_PX = 150

export const sanitizeChartFilename = (s: string): string =>
  s.replace(/[\\/:*?"<>|]+/g, '').replace(/\s+/g, ' ').trim()

export const chartPdfFilename = (chartName: string): string =>
  `${sanitizeChartFilename(chartName)} \u2014 ${LABELS.feature}.pdf`

export interface ExportChartNode {
  id: string
  position: { x: number; y: number }
  /** Card size as laid out on screen; cards vary in height by content/density. */
  width?: number
  height?: number
  data: { seat: SeatDetail; density?: CardDensity }
}

/**
 * Owner lines exactly as the role card shows them: saved assignment order,
 * the same "+N more" overflow, and no lines for an unassigned role.
 * allow-unsorted-people: never alphabetized.
 */
export function ownerLinesFor(seat: SeatDetail): string[] {
  const named = cardAssignees(seat).map(a => a.display_name)

  const hidden = hiddenAssigneeCount(seat)
  return hidden > 0 ? [...named, `+${hidden} more`] : named
}

const nodeWidth = (node: ExportChartNode) => node.width || PRINT_NODE_W_PX
const nodeHeight = (node: ExportChartNode) => node.height || PRINT_NODE_H_PX

export interface ExportChartEdge {
  id: string
  source: string
  target: string
}

export interface ExportChartPdfInput {
  nodes: ExportChartNode[]
  edges: ExportChartEdge[]
  ownersBox: SeatDetail | null
  chartName: string
  teamName: string
}

export interface ExportChartPdfDeps {
  loadJsPdf: () => Promise<new (opts: any) => any>
}

const defaultDeps: ExportChartPdfDeps = {
  loadJsPdf: async () => {
    const mod: any = await import('jspdf')
    return mod.jsPDF || mod.default
  },
}

export async function exportChartPdf(
  input: ExportChartPdfInput,
  deps: ExportChartPdfDeps = defaultDeps,
): Promise<string> {
  const { nodes, edges, ownersBox, chartName, teamName } = input
  const JsPDF = await deps.loadJsPdf()
  const pdf = new JsPDF({
    orientation: PDF_PAGE_ORIENTATION,
    unit: 'in',
    format: PDF_PAGE_FORMAT,
  })

  const areaX = PDF_MARGIN_IN
  const areaY = PDF_MARGIN_IN
  const areaW = PDF_PAGE_W_IN - 2 * PDF_MARGIN_IN
  const areaH = PDF_PAGE_H_IN - 2 * PDF_MARGIN_IN

  // ---- Header (vector text) ----
  let cursorY = areaY + 0.18
  pdf.setFont('helvetica', 'bold')
  pdf.setFontSize(13)
  pdf.setTextColor(0, 0, 0)
  pdf.text(chartName, areaX, cursorY, { baseline: 'alphabetic' })

  pdf.setFont('helvetica', 'normal')
  pdf.setFontSize(10)
  pdf.setTextColor(80, 80, 80)
  pdf.text(teamName, areaX + areaW, cursorY, {
    align: 'right',
    baseline: 'alphabetic',
  })

  cursorY += 0.06
  pdf.setDrawColor(170, 170, 170)
  pdf.setLineWidth(0.008)
  pdf.line(areaX, cursorY, areaX + areaW, cursorY)
  cursorY += 0.08

  // ---- Owner's Box row ----
  if (ownersBox) {
    const ownerH = 0.34
    pdf.setDrawColor(170, 170, 170)
    pdf.setLineWidth(0.01)
    pdf.roundedRect(areaX, cursorY, areaW, ownerH, 0.05, 0.05)

    pdf.setFont('helvetica', 'bold')
    pdf.setFontSize(8)
    pdf.setTextColor(110, 110, 110)
    pdf.text("OWNER'S BOX", areaX + 0.1, cursorY + 0.13)

    pdf.setFont('helvetica', 'bold')
    pdf.setFontSize(11)
    pdf.setTextColor(0, 0, 0)
    pdf.text(ownersBox.name, areaX + 0.1, cursorY + 0.27)

    if (ownersBox.assignments.length > 0) {
      pdf.setFont('helvetica', 'normal')
      pdf.setFontSize(10)
      pdf.setTextColor(60, 60, 60)
      const nameW = pdf.getTextWidth(ownersBox.name)
      pdf.text(
        `\u00B7 ${ownersBox.assignments.map(a => a.display_name).join(', ')}`,
        areaX + 0.1 + nameW + 0.1,
        cursorY + 0.27,
      )
    }
    cursorY += ownerH + 0.1
  }

  const chartAreaY = cursorY
  const chartAreaH = areaY + areaH - chartAreaY

  if (nodes.length > 0 && chartAreaH > 0) {
    const routes = familyRoutes(nodes.map(n => ({
      id: n.id, ...n.position, width: nodeWidth(n), height: nodeHeight(n),
    })), edges)
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
    for (const n of nodes) {
      if (n.position.x < minX) minX = n.position.x
      if (n.position.y < minY) minY = n.position.y
      if (n.position.x + nodeWidth(n) > maxX) maxX = n.position.x + nodeWidth(n)
      if (n.position.y + nodeHeight(n) > maxY) maxY = n.position.y + nodeHeight(n)
    }
    for (const route of routes.values()) for (const path of [...route.shared, route.branch]) {
      for (const command of path) for (let i = 0; i < command.values.length; i += 2) {
        minX = Math.min(minX, command.values[i]); maxX = Math.max(maxX, command.values[i])
        minY = Math.min(minY, command.values[i + 1]); maxY = Math.max(maxY, command.values[i + 1])
      }
    }
    const contentW = Math.max(1, maxX - minX)
    const contentH = Math.max(1, maxY - minY)

    // px -> in. Cap so small charts don't blow up text past readable.
    const scale = Math.min(areaW / contentW, chartAreaH / contentH, 1 / 72)

    const drawW = contentW * scale
    const drawH = contentH * scale
    const offX = areaX + (areaW - drawW) / 2
    const offY = chartAreaY + (chartAreaH - drawH) / 2

    const toX = (px: number) => offX + (px - minX) * scale
    const toY = (py: number) => offY + (py - minY) * scale

    // Same rounded commands, shared segments drawn exactly once, no guides.
    pdf.setDrawColor(133, 133, 133)
    const edgeWidth = Math.max(0.005, 1.5 * scale)
    pdf.setLineWidth(edgeWidth)
    for (const route of routes.values()) {
      for (const commands of [...route.shared, route.branch]) {
        pdf.path(commands.map(command => ({
          op: command.op.toLowerCase(),
          c: command.values.map((v, i) => i % 2 ? toY(v) : toX(v)),
        })))
        pdf.stroke()
      }
    }

    // ---- Nodes (vector rect + vector text) ----
    const padIn = 10 * scale
    const namePt = Math.max(5, 16 * scale * 72)
    const statusPt = Math.max(4, 11 * scale * 72)
    const assigneePt = Math.max(4, 12 * scale * 72)
    const nodeStroke = Math.max(0.005, 1.5 * scale)
    const radius = Math.min(0.06, 6 * scale)

    for (const n of nodes) {
      const seat = n.data.seat
      const x = toX(n.position.x)
      const y = toY(n.position.y)
      const nodeWIn = nodeWidth(n) * scale
      const nodeHIn = nodeHeight(n) * scale

      pdf.setDrawColor(51, 51, 51)
      pdf.setFillColor(255, 255, 255)
      pdf.setLineWidth(nodeStroke)
      pdf.roundedRect(x, y, nodeWIn, nodeHIn, radius, radius, 'FD')

      const textX = x + padIn
      const textW = nodeWIn - 2 * padIn
      const bottomY = y + nodeHIn - padIn
      let textY = y + padIn

      // Match the screen's single-line title so long names cannot consume the
      // status/responsibility space in compact, unassigned cards.
      pdf.setFont('helvetica', 'bold')
      pdf.setFontSize(namePt)
      pdf.setTextColor(0, 0, 0)
      const nameLineH = (namePt / 72) * 1.2
      const nameLines = (pdf.splitTextToSize(seat.name, textW) as string[]).slice(0, 1)
      nameLines.forEach((line, i) => {
        pdf.text(line, textX, textY + nameLineH * (i + 0.85))
      })
      textY += nameLineH * nameLines.length + nameLineH * 0.25

      // status — same rule as the card: a filled role prints no status line,
      // and the owners below it reflow up into the space.
      const statusLabel = showsStatusRow(seat)
        ? (STATUS_LABELS[seat.seat_status] || '').toUpperCase()
        : ''
      if (statusLabel && textY < bottomY) {
        const statusLineH = (statusPt / 72) * 1.2
        pdf.setFont('helvetica', 'normal')
        pdf.setFontSize(statusPt)
        pdf.setTextColor(68, 68, 68)
        pdf.text(statusLabel, textX, textY + statusLineH * 0.85)
        textY += statusLineH + statusLineH * 0.25
      }

      // owners — one full name per line, same order and overflow
      // as the card, at both densities
      const ownerLines = ownerLinesFor(seat)
      const assigneeLineH = (assigneePt / 72) * 1.3
      if (ownerLines.length > 0 && textY < bottomY) {
        pdf.setFont('helvetica', 'normal')
        pdf.setFontSize(assigneePt)
        pdf.setTextColor(0, 0, 0)
        const maxLines = Math.max(1, Math.floor((bottomY - textY) / assigneeLineH))
        const lines = ownerLines.slice(0, maxLines)
        lines.forEach((owner, i) => {
          // One line per owner, clipped to the box rather than wrapped, so the
          // card's fixed width holds exactly as it does on screen.
          const [line] = pdf.splitTextToSize(owner, textW) as string[]
          pdf.text(line, textX, textY + assigneeLineH * (i + 0.85))
        })
        textY += assigneeLineH * lines.length + assigneeLineH * 0.25
      }

      // responsibilities — same titles (and overflow indicator) as the card,
      // and like the card, only in expanded density
      const responsibilityTitles = cardResponsibilities(seat).map(r => `\u2022 ${r.title}`)
      const hiddenResponsibilities = hiddenResponsibilityCount(seat)
      if (hiddenResponsibilities > 0) responsibilityTitles.push(`+${hiddenResponsibilities} more`)
      if (responsibilityTitles.length === 0) {
        responsibilityTitles.push(`No ${LABELS.responsibilities.toLowerCase()} yet`)
      }
      if (n.data.density !== 'collapsed' && textY < bottomY) {
        pdf.setFont('helvetica', 'normal')
        pdf.setFontSize(statusPt)
        pdf.setTextColor(68, 68, 68)
        const lineH = (statusPt / 72) * 1.3
        const maxLines = Math.max(0, Math.floor((bottomY - textY) / lineH))
        responsibilityTitles.slice(0, maxLines).forEach((title, i) => {
          const [line] = pdf.splitTextToSize(title, textW) as string[]
          pdf.text(line, textX, textY + lineH * (i + 0.85))
        })
      }
    }
  }

  const filename = chartPdfFilename(chartName)
  pdf.save(filename)
  return filename
}
